import { useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStoreInfo, useStoreCatalog } from "../../../lib/query/queries/useStoreQueries";
import PropTypes from "prop-types";
import { ArrowLeft, BoxSeam, ShieldExclamation } from "react-bootstrap-icons";
import { useMerchantAuth } from "../../../utilities/authCheck";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

// ─── Module-level CSS ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("sc-styles")) {
  const s = document.createElement("style");
  s.id = "sc-styles";
  s.textContent = `
    @keyframes _sc_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._sc_sk { animation: _sc_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .sc-card { transition: transform 0.22s cubic-bezier(0.34,1.24,0.64,1), box-shadow 0.22s ease; cursor: pointer; }
    .sc-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.09) !important; }
  `;
  document.head.appendChild(s);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

const PAGE = { minHeight: "100vh", backgroundColor: "#F2F2F7", fontFamily: FONT };

// ─── Atoms ───────────────────────────────────────────────────────────────────

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_sc_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

// ─── ProductCard ─────────────────────────────────────────────────────────────

const ProductCard = ({
  loading,
  id,
  name,
  description,
  price,
  category,
  storeId,
}) => {
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);

  const handleMouseEnter = useCallback(() => setHover(true), []);
  const handleMouseLeave = useCallback(() => setHover(false), []);

  const navigateToProduct = useCallback(() => {
    if (!loading && storeId && id) {
      navigate(`/store/catalog/${storeId}/${id}`);
    }
  }, [loading, storeId, id, navigate]);

  if (loading) {
    return (
      <div style={{
        backgroundColor: "#fff", borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.08)", padding: "14px 16px",
      }}>
        <Skel w={58} h={11} r={5} style={{ marginBottom: 10 }} />
        <Skel w="78%" h={18} r={6} style={{ marginBottom: 8 }} />
        <Skel w={74} h={22} r={11} style={{ marginBottom: 10 }} />
        <Skel w="90%" h={12} r={5} style={{ marginBottom: 5 }} />
        <Skel w="65%" h={12} r={5} style={{ marginBottom: 16 }} />
        <Skel w={66} h={20} r={6} />
      </div>
    );
  }

  return (
    <div
      className="sc-card"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={navigateToProduct}
      style={{
        backgroundColor: "#fff",
        borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.09)",
        padding: "14px 16px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      {/* ID */}
      <span style={{
        fontSize: "0.7rem", fontWeight: 500,
        color: "rgba(60,60,67,0.33)", marginBottom: 7,
        display: "block", fontVariantNumeric: "tabular-nums",
      }}>
        #{id}
      </span>

      {/* Name */}
      <h3 style={{
        fontSize: "0.9375rem", fontWeight: 600,
        color: hover ? "#007AFF" : "#1c1c1e",
        margin: "0 0 8px", lineHeight: 1.3,
        letterSpacing: "-0.01em",
        display: "-webkit-box", WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical", overflow: "hidden",
        transition: "color 0.18s ease",
      }}>
        {name}
      </h3>

      {/* Category */}
      {category && (
        <span style={{
          display: "inline-flex", alignItems: "center",
          padding: "3px 10px", borderRadius: 20,
          backgroundColor: "rgba(60,60,67,0.07)",
          color: "rgba(60,60,67,0.58)",
          fontSize: "0.72rem", fontWeight: 500,
          marginBottom: 10, alignSelf: "flex-start",
          letterSpacing: "0.01em",
        }}>
          {category}
        </span>
      )}

      {/* Description */}
      {description && (
        <p style={{
          fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)",
          lineHeight: 1.45, margin: "0 0 12px", flex: 1,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {description}
        </p>
      )}

      {/* Price */}
      {price > 0 && (
        <div style={{
          marginTop: "auto", paddingTop: 10,
          borderTop: "1px solid rgba(60,60,67,0.07)",
        }}>
          <span style={{
            fontSize: "0.9375rem", fontWeight: 700,
            color: "#1c1c1e", letterSpacing: "-0.02em",
          }}>
            ${price.toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
};

ProductCard.propTypes = {
  loading: PropTypes.bool,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  imageSrc: PropTypes.string,
  price: PropTypes.number,
  category: PropTypes.string,
  storeId: PropTypes.string.isRequired,
  type: PropTypes.string,
  subType: PropTypes.string,
};

ProductCard.defaultProps = {
  loading: false,
  description: "",
  imageSrc: "",
  price: 0,
  category: "",
  type: "",
  subType: "",
};

// ─── StoreHeader ──────────────────────────────────────────────────────────────

const StoreHeader = ({ loading, store, productsCount }) => {
  if (loading) {
    return (
      <div style={{
        backgroundColor: "#fff", borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.08)",
        padding: "18px 20px", marginBottom: 20,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
          <Skel w={54} h={54} r={14} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Skel w="52%" h={20} r={6} style={{ marginBottom: 8 }} />
            <Skel w="36%" h={14} r={5} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Skel w={90} h={26} r={13} />
          <Skel w={102} h={26} r={13} />
        </div>
      </div>
    );
  }

  if (!store) return null;

  return (
    <div style={{
      backgroundColor: "#fff", borderRadius: 16,
      border: "1px solid rgba(60,60,67,0.09)",
      padding: "18px 20px", marginBottom: 20,
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        {store.imgSrc && (
          <img
            src={store.imgSrc}
            alt={store.store_name}
            style={{
              width: 54, height: 54, borderRadius: 14,
              objectFit: "cover", flexShrink: 0,
            }}
          />
        )}
        <div>
          <h3 style={{
            fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e",
            margin: "0 0 3px", letterSpacing: "-0.02em",
          }}>
            {store.store_name}
          </h3>
          <p style={{
            fontSize: "0.8125rem", color: "rgba(60,60,67,0.52)",
            margin: 0, fontWeight: 500,
          }}>
            {store.store_category}
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <span style={{
          padding: "4px 12px", borderRadius: 20,
          backgroundColor: "rgba(60,60,67,0.07)",
          color: "rgba(60,60,67,0.62)",
          fontSize: "0.78rem", fontWeight: 500,
        }}>
          {productsCount} Products
        </span>
        <span style={{
          padding: "4px 12px", borderRadius: 20,
          backgroundColor: "rgba(60,60,67,0.07)",
          color: "rgba(60,60,67,0.62)",
          fontSize: "0.78rem", fontWeight: 500,
        }}>
          {store.rating ? `${store.rating.toFixed(1)} ★` : "No ratings"}
        </span>
      </div>
    </div>
  );
};

StoreHeader.propTypes = {
  loading: PropTypes.bool,
  store: PropTypes.shape({
    store_name: PropTypes.string,
    store_category: PropTypes.string,
    imgSrc: PropTypes.string,
    rating: PropTypes.number,
  }),
  productsCount: PropTypes.number,
};

StoreHeader.defaultProps = {
  loading: false,
  store: null,
  productsCount: 0,
};

// ─── Catalog ──────────────────────────────────────────────────────────────────

const Catalog = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();

  const { isAuthorized, isLoading: authLoading, logout } = useMerchantAuth();

  // useEditProduct/useAddNewProduct (see productMutations) invalidate this
  // exact query key on success, so navigating back here after an edit/add
  // shows the change immediately instead of relying on this mount effect
  // alone to have caught it.
  const storeInfoQuery = useStoreInfo(storeId);
  const catalogQuery = useStoreCatalog(storeId, {
    // Preserves the original "Invalid data format received from server" guard.
    select: (data) => {
      if (!Array.isArray(data)) throw new Error("Invalid data format received from server");
      return data;
    },
  });

  const state = {
    products: catalogQuery.data ?? [],
    loading: catalogQuery.isLoading,
    error: catalogQuery.isError
      ? catalogQuery.error?.message || "Failed to load products. Please try again later."
      : null,
    store: storeInfoQuery.data ?? null,
    storeLoading: storeInfoQuery.isLoading,
  };

  const navigateToAddProduct = useCallback(() => {
    if (storeId) navigate(`/store/catalog/${storeId}/add`);
  }, [storeId, navigate]);

  const handleRetry = useCallback(() => {
    catalogQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const renderContent = () => {
    if (state.loading) {
      return (
        <div className="row g-3">
          {Array(6).fill(null).map((_, i) => (
            <div key={i} className="col-12 col-md-6 col-lg-4">
              <ProductCard loading storeId={storeId} id={`skeleton-${i}`} name="" />
            </div>
          ))}
        </div>
      );
    }

    if (state.error) {
      return (
        <div style={{
          backgroundColor: "rgba(255,59,48,0.08)", borderRadius: 12,
          padding: "14px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span style={{ fontSize: "0.875rem", color: "#FF3B30" }}>{state.error}</span>
          <button
            onClick={handleRetry}
            style={{
              background: "none", border: "none", padding: 0,
              fontSize: "0.8125rem", color: "#007AFF", fontWeight: 600, cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    if (state.products.length === 0) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          padding: "64px 24px", textAlign: "center",
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            backgroundColor: "rgba(60,60,67,0.06)",
            display: "flex", alignItems: "center", justifyContent: "center",
            marginBottom: 16,
          }}>
            <BoxSeam size={26} color="rgba(60,60,67,0.28)" />
          </div>
          <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 6 }}>
            No products yet
          </h4>
          <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.45)", margin: 0 }}>
            Click "Add product" to create your first product.
          </p>
        </div>
      );
    }

    return (
      <div className="row g-3">
        {state.products.map((item) => (
          <div key={item.id} className="col-12 col-md-6 col-lg-4">
            <ProductCard loading={false} storeId={storeId} {...item} />
          </div>
        ))}
      </div>
    );
  };

  // Auth loading — skeleton page
  if (authLoading) {
    return (
      <div style={PAGE}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 16px 40px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <Skel w={34} h={34} r={17} />
            <Skel w={140} h={24} r={7} />
          </div>
          <div style={{
            backgroundColor: "#fff", borderRadius: 16,
            border: "1px solid rgba(60,60,67,0.08)", padding: "18px 20px", marginBottom: 20,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
              <Skel w={54} h={54} r={14} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <Skel w="50%" h={20} r={6} style={{ marginBottom: 8 }} />
                <Skel w="34%" h={14} r={5} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Skel w={88} h={26} r={13} />
              <Skel w={100} h={26} r={13} />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <Skel w={80} h={22} r={7} />
            <Skel w={100} h={32} r={16} />
          </div>
          <div className="row g-3">
            {Array(6).fill(null).map((_, i) => (
              <div key={i} className="col-12 col-md-6 col-lg-4">
                <div style={{
                  backgroundColor: "#fff", borderRadius: 16,
                  border: "1px solid rgba(60,60,67,0.08)", padding: "14px 16px",
                }}>
                  <Skel w={58} h={11} r={5} style={{ marginBottom: 10 }} />
                  <Skel w="78%" h={18} r={6} style={{ marginBottom: 8 }} />
                  <Skel w={74} h={22} r={11} style={{ marginBottom: 10 }} />
                  <Skel w="90%" h={12} r={5} style={{ marginBottom: 5 }} />
                  <Skel w="65%" h={12} r={5} style={{ marginBottom: 16 }} />
                  <Skel w={66} h={20} r={6} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Unauthorized
  if (!isAuthorized) {
    return (
      <div style={PAGE}>
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", minHeight: "80vh", padding: 32,
        }}>
          <div style={{
            width: 60, height: 60, borderRadius: 18,
            backgroundColor: "rgba(255,149,0,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            marginBottom: 16,
          }}>
            <ShieldExclamation size={28} color="#FF9500" />
          </div>
          <h3 style={{
            fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e",
            marginBottom: 6, textAlign: "center",
          }}>
            Access Restricted
          </h3>
          <p style={{
            fontSize: "0.875rem", color: "rgba(60,60,67,0.5)",
            textAlign: "center", marginBottom: 24,
          }}>
            Merchant authentication required.
          </p>
          <button
            onClick={logout}
            style={{
              padding: "11px 28px", borderRadius: 12, border: "none",
              backgroundColor: "#007AFF", color: "#fff",
              fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer",
              fontFamily: FONT,
            }}
          >
            Sign In as Merchant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE}>
      <Seo {...generateNoIndexSEO("Store Catalog")} />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 16px 48px" }}>

        {/* Back nav + title */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              width: 34, height: 34, borderRadius: "50%",
              background: "rgba(60,60,67,0.08)", border: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#1c1c1e", flexShrink: 0,
              transition: "background 0.15s ease",
            }}
          >
            <ArrowLeft size={16} />
          </button>
          <h2 style={{
            fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e",
            letterSpacing: "-0.03em", margin: 0,
          }}>
            Store Catalog
          </h2>
        </div>

        {/* Store header */}
        <StoreHeader
          loading={state.storeLoading}
          store={state.store}
          productsCount={state.products?.length || 0}
        />

        {/* Products section header */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 16,
        }}>
          <h3 style={{
            fontSize: "1.0625rem", fontWeight: 600, color: "#1c1c1e",
            letterSpacing: "-0.02em", margin: 0,
          }}>
            Products
          </h3>
          <button
            onClick={navigateToAddProduct}
            disabled={!storeId}
            style={{
              padding: "8px 18px", borderRadius: 20, border: "none",
              backgroundColor: "#007AFF", color: "#fff",
              fontSize: "0.8125rem", fontWeight: 600,
              cursor: storeId ? "pointer" : "not-allowed",
              opacity: storeId ? 1 : 0.5,
              transition: "opacity 0.15s ease",
              fontFamily: FONT,
            }}
          >
            + Add product
          </button>
        </div>

        {/* Content */}
        {renderContent()}

      </div>
    </div>
  );
};

export default Catalog;
